import { useMemo, useState } from 'react';
import { AutocompleteInput } from '@/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetPickupCommercesQuery } from '@/pages/Business/pages/PickupCycles/services';
import { PickupCommerceOption } from '@/pages/Business/pages/PickupCycles/models';

interface CommerceFilterAutocompleteProps {
  value: string;
  onChange: (commerceId: string) => void;
}

const isCommerce = (data: unknown): data is PickupCommerceOption =>
  typeof data === 'object' && data !== null && 'id' in data;

// Máximo de opciones a renderizar por búsqueda (los comercios se cargan todos y
// se filtran en el front, de forma incremental a medida que se escribe).
const MAX_OPTIONS = 50;

const CommerceFilterAutocomplete = ({
  value,
  onChange,
}: CommerceFilterAutocompleteProps) => {
  const [input, setInput] = useState('');
  const { data: commerces = [], isLoading } = useGetPickupCommercesQuery();

  const options: Option[] = useMemo(() => {
    const query = input.trim().toLowerCase();
    const filtered = query
      ? commerces.filter((commerce) =>
          commerce.name.toLowerCase().includes(query),
        )
      : commerces;
    return filtered
      .slice(0, MAX_OPTIONS)
      .map((commerce) => ({ label: commerce.name, data: commerce }));
  }, [commerces, input]);

  const selectedOption: Option | null = value
    ? {
        label:
          commerces.find((commerce) => commerce.id === value)?.name ??
          'Comercio seleccionado',
        data: value,
      }
    : null;

  const handleChange = (option: Option | null) => {
    if (option && isCommerce(option.data)) {
      onChange(option.data.id);
      return;
    }
    onChange('');
  };

  return (
    <AutocompleteInput
      name='commerceFilter'
      placeholder={isLoading ? 'Cargando comercios...' : 'Comercio'}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={setInput}
      options={options}
      loading={isLoading}
      showTypingLengthHint={false}
    />
  );
};

export default CommerceFilterAutocomplete;
